import React, { useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import './Home.css';

function Home() {
    const navigate = useNavigate();
    const observerRef = useRef(null);

    useEffect(() => {
        // Handle back button redirect to talktokrishna.ai
        // This ensures that if a user lands on this page and presses back,
        // they are redirected to the main portal instead of exiting or going to previous history.
        const redirectUrl = 'https://talktokrishna.ai';

        const pushRedirectState = () => {
            if (!window.history.state?.isForcedRedirect) {
                // Merge with existing state to avoid breaking React Router
                const newState = { ...(window.history.state || {}), isForcedRedirect: true };
                window.history.pushState(newState, '', window.location.href);
            }
        };

        const handlePopState = (event) => {
            // If the user goes back, land them on the main portal
            window.location.replace(redirectUrl);
        };

        const handlePageShow = (event) => {
            if (event.persisted || !window.history.state?.isForcedRedirect) {
                pushRedirectState();
            }
        };

        // Initialize with a slight delay to let the browser settle
        const timeoutId = setTimeout(pushRedirectState, 500);

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('pageshow', handlePageShow);

        // Intersection Observer for scroll animations
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe all animated elements
        const animatedElements = document.querySelectorAll('.fade-in-section');
        animatedElements.forEach((el) => observerRef.current.observe(el));

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('pageshow', handlePageShow);
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        // 1. Push a dummy state so there's a "back" to go to
        window.history.pushState(null, null, window.location.href);

        // 2. Listen for the back button (popstate)
        const handlePopState = () => {
            // 3. Redirect to the main domain
            window.location.replace('https://talktokrishna.ai');
        };

        window.addEventListener('popstate', handlePopState);

        // Cleanup when component unmounts
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    return (
        <div className="home-page">
            <Navbar />

            {/* Floating Elements Background */}
            <div className="floating-elements">
                <div className="float-element om-symbol">🕉️</div>
                <div className="float-element lotus">🪷</div>
                <div className="float-element peacock">🦚</div>
                <div className="float-element om-symbol-2">ॐ</div>
            </div>

            {/* Hero Section */}
            <section className="hero-section-premium">
                <div className="container hero-grid">
                    <div className="hero-content-left">
                        <div className="badge-premium">
                            <span className="badge-dot"></span>
                            Guía espiritual basada en IA
                        </div>

                        <h1 className="hero-headline-premium">
                            Sabiduría antigua,
                            <br />
                            <span className="gradient-text-animated">voz moderna</span>
                        </h1>

                        <p className="hero-description-premium">
                            Experimenta las enseñanzas eternas del Bhagavad Gita con tecnología avanzada de voz IA.
                            Obtén guía espiritual personalizada a través de un diálogo en tiempo real con Krishna.
                        </p>

                        <div className="hero-cta-group">
                            <button className="btn-premium-primary" onClick={() => navigate('/chat')}>
                                <span className="btn-icon">🎙️</span>
                                Iniciar charla
                                <span className="btn-arrow">→</span>
                            </button>
                            <button className="btn-premium-secondary" onClick={() => navigate('/about')}>
                                <span className="btn-icon">📖</span>
                                Saber más
                            </button>
                        </div>

                        <div className="stats-row">
                            <div className="stat-item">
                                <div className="stat-number">700+</div>
                                <div className="stat-label">Versos</div>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <div className="stat-number">24/7</div>
                                <div className="stat-label">Disponible</div>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <div className="stat-number">∞</div>
                                <div className="stat-label">Sabiduría</div>
                            </div>
                        </div>
                    </div>

                    <div className="hero-visual-right">
                        {/* 3D Orb Illustration */}
                        <div className="orb-container-3d">
                            <div className="orb-main">
                                <div className="orb-inner-glow"></div>
                                <div className="orb-particles">
                                    {/* 8 particles — visually identical to 20, but 60% fewer GPU layers */}
                                    {[...Array(8)].map((_, i) => (
                                        <div key={i} className={`particle p-${i}`}></div>
                                    ))}
                                </div>
                                <div className="orb-rings">
                                    <div className="ring ring-1"></div>
                                    <div className="ring ring-2"></div>
                                    <div className="ring ring-3"></div>
                                </div>
                                <div className="orb-center-icon">🕉️</div>
                            </div>

                            {/* Floating Cards */}
                            <div className="floating-card card-1">
                                <div className="card-icon">🎯</div>
                                <div className="card-content">
                                    <strong>Al instante</strong>
                                    <span>Respuestas IA</span>
                                </div>
                            </div>

                            <div className="floating-card card-2">
                                <div className="card-icon">🧘</div>
                                <div className="card-content">
                                    <strong>Crecimiento</strong>
                                    <span>Camino espiritual</span>
                                </div>
                            </div>

                            <div className="floating-card card-3">
                                <div className="card-icon">💬</div>
                                <div className="card-content">
                                    <strong>Chat</strong>
                                    <span>Voz real</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section - Premium Design */}
            <section className="features-section-premium fade-in-section">
                <div className="container">
                    <div className="section-header-premium">
                        <span className="section-badge">Características</span>
                        <h2 className="section-title-premium">
                            Por qué elegir <span className="gradient-text">Hablar con Krishna</span><span className="trademark">®</span>
                        </h2>
                        <p className="section-subtitle">
                            Combinando sabiduría antigua e IA moderna para tu camino espiritual
                        </p>
                    </div>

                    <div className="features-grid-premium">
                        <div className="feature-card-premium">
                            <div className="feature-icon-wrapper">
                                <div className="feature-icon-bg"></div>
                                <div className="feature-icon">🎙️</div>
                            </div>
                            <h3>Chat de voz con IA</h3>
                            <p>Comunícate de forma natural en español o inglés y recibe respuestas con voz real gracias a la tecnología TTS neuronal.</p>
                            <div className="feature-tags">
                                <span className="tag">Tiempo real</span>
                                <span className="tag">Natural</span>
                            </div>
                        </div>

                        <div className="feature-card-premium featured">
                            <div className="featured-badge">Más popular</div>
                            <div className="feature-icon-wrapper">
                                <div className="feature-icon-bg"></div>
                                <div className="feature-icon">📜</div>
                            </div>
                            <h3>Citas precisas</h3>
                            <p>Todas las respuestas se basan en versos auténticos del Bhagavad Gita utilizando la arquitectura RAG.</p>
                            <div className="feature-tags">
                                <span className="tag">Verificado</span>
                                <span className="tag">Auténtico</span>
                            </div>
                        </div>

                        <div className="feature-card-premium">
                            <div className="feature-icon-wrapper">
                                <div className="feature-icon-bg"></div>
                                <div className="feature-icon">✨</div>
                            </div>
                            <h3>Enfoque personalizado</h3>
                            <p>Recibe consejos adaptados a tus situaciones de vida y estado emocional.</p>
                            <div className="feature-tags">
                                <span className="tag">Individual</span>
                                <span className="tag">Contextual</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works-section fade-in-section">
                <div className="container">
                    <div className="section-header-premium">
                        <span className="section-badge">Proceso</span>
                        <h2 className="section-title-premium">
                            Cómo <span className="gradient-text">funciona</span>
                        </h2>
                    </div>

                    <div className="steps-container">
                        <div className="step-card">
                            <div className="step-number">01</div>
                            <div className="step-icon">🎤</div>
                            <h3>Haz tu pregunta</h3>
                            <p>Pregunta lo que quieras: sobre la vida, el dharma, el karma o busca guía espiritual</p>
                        </div>

                        <div className="step-connector"></div>

                        <div className="step-card">
                            <div className="step-number">02</div>
                            <div className="step-icon">🧠</div>
                            <h3>La IA procesa</h3>
                            <p>Nuestro sistema RAG analiza más de 700 versos para encontrar la mejor respuesta</p>
                        </div>

                        <div className="step-connector"></div>

                        <div className="step-card">
                            <div className="step-number">03</div>
                            <div className="step-icon">🔊</div>
                            <h3>Recibe sabiduría</h3>
                            <p>Escucha la guía auténtica basada en la Gita con la voz de Krishna</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section-premium fade-in-section">
                <div className="container">
                    <div className="cta-card-premium">
                        <div className="cta-content">
                            <h2>¿Listo para iniciar tu camino espiritual?</h2>
                            <p>Únete a miles de personas que buscan sabiduría a través de la charla con IA.</p>
                            <button className="btn-premium-primary btn-large" onClick={() => navigate('/chat')}>
                                <span className="btn-icon">🕉️</span>
                                Iniciar conversación con Krishna
                                <span className="btn-arrow">→</span>
                            </button>
                        </div>
                        <div className="cta-decoration">
                            <div className="decoration-circle c1"></div>
                            <div className="decoration-circle c2"></div>
                            <div className="decoration-circle c3"></div>
                        </div>
                    </div>
                </div>
            </section>




        </div>
    );
}

export default Home;
